import { useState, useMemo, useEffect } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Calendar, ChevronDown, X } from "lucide-react";

// YYYY-MM-DD helper (local timezone)
const fmtISO = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const fmtDisplay = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  const date = new Date(Number(y), Number(m) - 1, Number(d));
  return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

// Preset range computers (use local date)
const presetRanges = () => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  const last7Start = new Date(today); last7Start.setDate(today.getDate() - 6);
  const last30Start = new Date(today); last30Start.setDate(today.getDate() - 29);
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const prevMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const prevMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
  return [
    { id: "today", label: "Today", from: fmtISO(today), to: fmtISO(today) },
    { id: "yesterday", label: "Yesterday", from: fmtISO(yesterday), to: fmtISO(yesterday) },
    { id: "last7", label: "Last 7 Days", from: fmtISO(last7Start), to: fmtISO(today) },
    { id: "last30", label: "Last 30 Days", from: fmtISO(last30Start), to: fmtISO(today) },
    { id: "thisMonth", label: "This Month", from: fmtISO(monthStart), to: fmtISO(today) },
    { id: "prevMonth", label: "Previous Month", from: fmtISO(prevMonthStart), to: fmtISO(prevMonthEnd) },
  ];
};

// Detect which preset (if any) matches the active from/to range
const detectPreset = (from, to) => {
  const presets = presetRanges();
  const match = presets.find(p => p.from === from && p.to === to);
  return match ? match.id : (from || to ? "custom" : "");
};

/**
 * Shopify-style date range filter.
 * Props:
 *  - from / to: ISO date strings (YYYY-MM-DD), can be empty
 *  - onChange: (from, to) => void — invoked instantly on preset/custom apply
 *  - allowEmpty: if true, "All time" / clear option is available (default true)
 *  - align: popover alignment ("start" | "end") - default "start"
 *  - testId: prefix for data-testid attributes
 */
export function DateRangeFilter({ from = "", to = "", onChange, allowEmpty = true, align = "start", testId = "date-range" }) {
  const [open, setOpen] = useState(false);
  const [customFrom, setCustomFrom] = useState(from);
  const [customTo, setCustomTo] = useState(to);

  useEffect(() => { setCustomFrom(from); setCustomTo(to); }, [from, to]);

  const presets = useMemo(() => presetRanges(), []);
  const activePreset = useMemo(() => detectPreset(from, to), [from, to]);

  const triggerLabel = useMemo(() => {
    if (!from && !to) return "All time";
    const matched = presets.find(p => p.id === activePreset);
    if (matched) return matched.label;
    if (from && to) return `${fmtDisplay(from)} – ${fmtDisplay(to)}`;
    if (from) return `From ${fmtDisplay(from)}`;
    return `Until ${fmtDisplay(to)}`;
  }, [from, to, activePreset, presets]);

  const applyPreset = (p) => {
    onChange(p.from, p.to);
    setOpen(false);
  };

  const applyCustom = () => {
    if (customFrom && customTo && customFrom > customTo) return;
    onChange(customFrom || "", customTo || "");
    setOpen(false);
  };

  const clearAll = () => {
    setCustomFrom(""); setCustomTo("");
    onChange("", "");
    setOpen(false);
  };

  const isActive = !!(from || to);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={`h-8 px-3 text-xs font-medium border-[#E8E8E8] hover:bg-slate-50 ${isActive ? "border-accent text-accent" : "text-slate-700"}`}
          data-testid={`${testId}-trigger`}
        >
          <Calendar className="h-3.5 w-3.5 mr-2" />
          <span className="truncate max-w-[200px]">{triggerLabel}</span>
          <ChevronDown className="h-3.5 w-3.5 ml-2 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[420px] p-0 overflow-hidden" align={align}>
        <div className="flex">
          {/* Preset Sidebar */}
          <div className="w-[150px] border-r border-slate-100 bg-slate-50/60 py-2">
            {presets.map(p => {
              const selected = activePreset === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => applyPreset(p)}
                  className={`w-full text-left px-3 py-2 text-xs transition-colors ${selected ? "bg-accent/10 text-accent font-semibold" : "text-slate-700 hover:bg-slate-100"}`}
                  data-testid={`${testId}-preset-${p.id}`}
                >
                  {p.label}
                </button>
              );
            })}
            <button
              type="button"
              onClick={() => { /* focus the custom inputs */ }}
              className={`w-full text-left px-3 py-2 text-xs transition-colors ${activePreset === "custom" ? "bg-accent/10 text-accent font-semibold" : "text-slate-700 hover:bg-slate-100"}`}
              data-testid={`${testId}-preset-custom`}
            >
              Custom Range
            </button>
          </div>

          {/* Custom Range Panel */}
          <div className="flex-1 p-4 space-y-3">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Custom Date Range</div>
            <div className="space-y-2">
              <div>
                <label className="text-[11px] text-slate-500 mb-1 block">Start date</label>
                <Input
                  type="date"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  className="h-9 text-xs border-[#E8E8E8]"
                  max={customTo || undefined}
                  data-testid={`${testId}-from-input`}
                />
              </div>
              <div>
                <label className="text-[11px] text-slate-500 mb-1 block">End date</label>
                <Input
                  type="date"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                  className="h-9 text-xs border-[#E8E8E8]"
                  min={customFrom || undefined}
                  data-testid={`${testId}-to-input`}
                />
              </div>
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-slate-100">
              {allowEmpty ? (
                <button
                  type="button"
                  onClick={clearAll}
                  className="text-[11px] text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
                  data-testid={`${testId}-clear`}
                >
                  <X className="h-3 w-3" /> Clear
                </button>
              ) : <span />}
              <Button
                size="sm"
                className="h-8 text-xs bg-accent hover:bg-accent/90"
                onClick={applyCustom}
                disabled={!customFrom && !customTo}
                data-testid={`${testId}-apply`}
              >
                Apply
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default DateRangeFilter;
